/**
 * Area (g) · Facturation SaaS (FS-*) + paramètres plateforme (invoicing launch switch).
 * OWNED BY the area-saas agent (see admin/README.md « Areas & ownership »).
 *
 * The invoicing product the clients use (web `/app/facturation`, API `domains/invoicing`) seen from the back-office:
 * accounts = companies with the invoicing service, their plan / quotas / subscription, their documents and Iopole
 * e-invoicing transmissions, the incidents that block them, and the product defaults.
 *
 * Entity schemas, labels and prices come from ../shared/invoicing/common.ts (PLAN_META, EXTRA_SEAT_PRICE_CENTS,
 * EInvoiceStatus, INVOICE_STATUS_META…): never redefined here. Server work (api/docs/backoffice.md « Invoicing: no
 * team-side action exists »): every endpoint below except the platform switch is NEW — a read model over the
 * invoicing tables + an `invoicing_incidents` table + the admin actions listed per endpoint.
 */
import { z } from "zod";
import { adminEndpoint } from "../endpoint.ts";
import type { EndpointGroup } from "../shell.ts";
import { Cents, Email, Id, IsoDate, IsoDateTime, paginated } from "../common.ts";
import { PlatformFeatures } from "../shared/platform.ts";
import { BillingCycle, EInvoiceStatus, InvoicingPlan, QuotaKind } from "../shared/invoicing/common.ts";
import { StaffRole } from "../permissions.ts";

/* ═════════════════════════ Platform launch switches (/plateforme) ═════════════════════════ */

/**
 * Launch switches: every product ships dark and the admin owns the toggle. Reading is the public
 * `GET /platform/features` (shared/platform.ts); writing is this endpoint, ADMIN only (capability "platform"),
 * audited (ADMIN_FEATURE_FLAG_SET). Switching invoicing on reveals the 3-service designs on web.
 * `note`: optional reason kept in the audit trail (server: `setPlatformFeature(db, key, on, { by, now, note })`,
 * note column to add). A PATCH that does not change anything answers 409 `unchanged` (another admin was faster).
 */
export const PlatformFeaturesPatch = PlatformFeatures.partial().extend({ note: z.string().trim().max(300).optional() });

export const PlatformFeatureKey = z.enum(Object.keys(PlatformFeatures.shape) as [keyof PlatformFeatures, ...(keyof PlatformFeatures)[]]);
export type PlatformFeatureKey = z.infer<typeof PlatformFeatureKey>;

/** One toggle of a launch switch: who, when, which way, why. NEW server work: `platform_feature_events` table. */
export const PlatformFeatureEvent = z.object({
  id: Id,
  feature: PlatformFeatureKey,
  enabled: z.boolean(),
  at: IsoDateTime,
  by: z.object({ id: Id.nullable(), name: z.string(), role: StaffRole.nullable() }),
  note: z.string().nullable(),
  /** Invoicing accounts at the time of the toggle (their data is kept either way). */
  accountsAtToggle: z.number().int().nullable(),
});
export type PlatformFeatureEvent = z.infer<typeof PlatformFeatureEvent>;

export const PlatformFeaturesHistory = z.object({
  events: z.array(PlatformFeatureEvent),
  /** Accounts that hold invoicing data today (told in the confirmation window). */
  invoicingAccounts: z.number().int(),
});

/* ═════════════════════════ Shared account pieces ═════════════════════════ */

/** « Actif » · « Quota atteint » · « Accès restreint » · « Suspendu » (FS-Comptes « Statuts du compte »). */
export const SaasAccountStatus = z.enum(["active", "quota_reached", "restricted", "suspended"]);
export type SaasAccountStatus = z.infer<typeof SaasAccountStatus>;

/** Iopole enrolment of the account: Raccordé · Enrôlement en cours · Non raccordé. */
export const SaasEnrollment = z.enum(["registered", "in_progress", "not_connected"]);
export type SaasEnrollment = z.infer<typeof SaasEnrollment>;

export const SaasUsage = z.object({ used: z.number().int(), limit: z.number().int().nullable() });
export const SaasQuotas = z.object({ invoices: SaasUsage, quotes: SaasUsage, customers: SaasUsage, users: SaasUsage });
export type SaasQuotas = z.infer<typeof SaasQuotas>;

export const SaasDocCounts = z.object({ invoices: z.number().int(), quotes: z.number().int(), creditNotes: z.number().int() });

/** Month filter « Septembre 2026 » (YYYY-MM). */
export const Month = z.string().regex(/^\d{4}-\d{2}$/);

/* ═════════════════════════ FS-Comptes · accounts ═════════════════════════ */

export const SaasAccountsQuery = z.object({
  q: z.string().trim().optional(),
  plan: InvoicingPlan.optional(),
  status: SaasAccountStatus.optional(),
  /** `error` = untreated REJECTED / UNACCEPTABLE transmissions. */
  einv: z.enum(["registered", "in_progress", "not_connected", "error"]).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  /** At least one START counter at its limit. */
  quota: z.coerce.boolean().optional(),
  /** Restricts document counters and the last document to that month. */
  month: Month.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type SaasAccountsQuery = z.input<typeof SaasAccountsQuery>;

export const SaasAccountRow = z.object({
  companyId: Id,
  name: z.string(),
  legalForm: z.string(),
  city: z.string(),
  siret: z.string().nullable(),
  plan: InvoicingPlan,
  cycle: BillingCycle.nullable(),
  status: SaasAccountStatus,
  subscribedAt: IsoDate,
  /** Paid plans: next renewal (« renouvelé le 03/10 ») or end of the paid period (« jusqu'au 14/08/2027 »). */
  paidUntil: IsoDate.nullable(),
  /** Restricted: when the subscription lapsed (« expiré le 20/09 »). */
  expiredAt: IsoDate.nullable(),
  quotas: SaasQuotas,
  documents: SaasDocCounts,
  enrollment: SaasEnrollment,
  openRejects: z.number().int(),
  lastDocument: z.object({ number: z.string(), at: IsoDateTime }).nullable(),
});
export type SaasAccountRow = z.infer<typeof SaasAccountRow>;

export const SaasAccountsPage = paginated(SaasAccountRow).extend({
  /** Segment and pill counters, computed with the other filters. */
  counts: z.object({
    all: z.number().int(),
    start: z.number().int(),
    pro: z.number().int(),
    business: z.number().int(),
    quotaReached: z.number().int(),
    restricted: z.number().int(),
    suspended: z.number().int(),
    einvoiceError: z.number().int(),
  }),
  /** Accounts on the platform, no filter (« Aucun compte facturation » vs « Aucun compte » for a search). */
  platformTotal: z.number().int(),
});
export type SaasAccountsPage = z.infer<typeof SaasAccountsPage>;

/* ═════════════════════════ FS-Fiche · one account ═════════════════════════ */

export const SaasDocKind = z.enum(["invoice", "quote", "credit_note"]);
export type SaasDocKind = z.infer<typeof SaasDocKind>;

/** A document as the back-office lists it. `status` is the invoicing status key of its kind (common.ts metas). */
export const SaasDocumentRow = z.object({
  id: Id,
  kind: SaasDocKind,
  number: z.string(),
  customer: z.string(),
  issuedAt: IsoDateTime,
  totalTtcCents: Cents,
  status: z.string(),
  /** null for quotes and for individuals / foreign customers (cell « — »). */
  einvoice: EInvoiceStatus.nullable(),
  sent: z.boolean(),
  unlockCount: z.number().int(),
  /** Finalised invoice, never sent nor transmitted, under 3 unlocks. */
  unlockable: z.boolean(),
  /** Rejected or not transmitted invoice of an enrolled account. */
  retryable: z.boolean(),
});
export type SaasDocumentRow = z.infer<typeof SaasDocumentRow>;

export const SaasTransmissionRow = z.object({
  documentId: Id,
  number: z.string(),
  status: EInvoiceStatus,
  message: z.string(),
  at: IsoDateTime,
});
export type SaasTransmissionRow = z.infer<typeof SaasTransmissionRow>;

export const SaasIncidentType = z.enum(["support", "reminder", "email", "einvoice"]);
export type SaasIncidentType = z.infer<typeof SaasIncidentType>;
export const SaasIncidentState = z.enum(["open", "in_progress", "resolved"]);
export type SaasIncidentState = z.infer<typeof SaasIncidentState>;

export const SaasIncidentRow = z.object({
  id: Id,
  type: SaasIncidentType,
  title: z.string(),
  detail: z.string(),
  companyId: Id,
  companyName: z.string(),
  openedAt: IsoDateTime,
  state: SaasIncidentState,
  /** Red dot (failed reminder, rejection, undelivered e-mail) or amber (support request). */
  severity: z.enum(["high", "normal"]),
});
export type SaasIncidentRow = z.infer<typeof SaasIncidentRow>;

export const SaasHistoryEvent = z.object({ at: IsoDateTime, label: z.string(), detail: z.string().nullable(), tone: z.enum(["neutral", "success", "warning", "danger"]) });

export const SaasAccount = SaasAccountRow.extend({
  owner: z.object({ name: z.string(), email: z.string() }),
  clientSince: IsoDate,
  lastLoginAt: IsoDateTime.nullable(),
  usersCount: z.number().int(),
  enrolledAt: IsoDate.nullable(),
  autoRenew: z.boolean(),
  payment: z.object({ kind: z.enum(["card", "sepa"]), label: z.string() }).nullable(),
  lastRenewal: z.object({ failedAt: IsoDate, reason: z.string() }).nullable(),
  suspension: z.object({ at: IsoDateTime, reason: z.string().nullable(), by: z.string() }).nullable(),
  /** « Facturé par EXPERTISE ADMIN · facture ABO-2026-000418 » (Finance › factures émises). */
  billedBy: z.object({ issuer: z.string(), invoiceNumber: z.string() }).nullable(),
  signatureCredits: z.object({ remaining: z.number().int(), total: z.number().int(), since: IsoDate.nullable() }).nullable(),
  history: z.array(SaasHistoryEvent),
  transmissions: z.array(SaasTransmissionRow),
  incidents: z.array(SaasIncidentRow),
  /** First finalised invoice that can be unlocked (« Débloquer un document » of the support card). */
  unlockCandidateId: Id.nullable(),
  /** First rejected / not transmitted invoice (« Renvoyer à Iopole »). */
  retryCandidateId: Id.nullable(),
});
export type SaasAccount = z.infer<typeof SaasAccount>;

export const SaasDocTab = z.enum(["all", "invoices", "quotes", "credit_notes", "watch"]);
export type SaasDocTab = z.infer<typeof SaasDocTab>;

export const SaasDocumentsQuery = z.object({
  tab: SaasDocTab.default("all"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(8),
});

export const SaasDocumentsPage = paginated(SaasDocumentRow).extend({
  counts: z.object({ all: z.number().int(), invoices: z.number().int(), quotes: z.number().int(), creditNotes: z.number().int(), watch: z.number().int() }),
});

/** Document drawer (FS-Fiche « Détail d'un document », FS-Iopole « Historique d'une transmission »). */
export const SaasDocument = SaasDocumentRow.extend({
  companyId: Id,
  companyName: z.string(),
  companySiret: z.string().nullable(),
  customerDetail: z.string(),
  totalHtCents: Cents,
  vatCents: Cents,
  vatRate: z.number(),
  dueDate: IsoDate.nullable(),
  reminderDays: z.number().int().nullable(),
  file: z.object({ profile: z.string(), sizeLabel: z.string(), transmissionId: z.string().nullable() }).nullable(),
  lastTransmission: z.object({ status: EInvoiceStatus, code: z.string().nullable(), at: IsoDateTime, message: z.string() }).nullable(),
  history: z.array(SaasHistoryEvent.extend({ code: z.string().nullable() })),
  webhooks: z.array(z.object({ event: z.string(), at: IsoDateTime, ack: z.number().int(), signatureValid: z.boolean(), eventId: z.string(), destType: z.string() })),
});
export type SaasDocument = z.infer<typeof SaasDocument>;

const ByCompany = z.object({ companyId: Id });
const ByDocument = z.object({ companyId: Id, documentId: Id });

/** Contracts' own bounds (credit-notes.ts UNLOCK_REASON_MIN / MAX), repeated as literals: shared/ is a verbatim copy. */
export const UnlockBody = z.object({ reason: z.string() });
export const ResetQuotaBody = z.object({ quota: QuotaKind, reason: z.string() });
export const SuspendBody = z.object({ reason: z.string().trim().max(500).optional() });

export const ExportFile = z.object({ fileName: z.string(), rows: z.number().int(), sizeLabel: z.string(), mime: z.string(), content: z.string() });
export type ExportFile = z.infer<typeof ExportFile>;

/* ═════════════════════════ FS-Vue · overview ═════════════════════════ */

export const SaasOverview = z.object({
  month: Month,
  /** Months the selector offers, most recent first. */
  months: z.array(Month),
  accounts: z.object({ total: z.number().int(), start: z.number().int(), pro: z.number().int(), business: z.number().int() }),
  documents: SaasDocCounts.extend({ previous: SaasDocCounts }),
  flowToday: z.object({ submitted: z.number().int(), inProgress: z.number().int(), rejected: z.number().int(), notTransmitted: z.number().int() }),
  incidents: z.object({ open: z.number().int(), support: z.number().int(), reminders: z.number().int(), emails: z.number().int() }),
  quotaReached: z.number().int(),
  plans: z.array(z.object({ plan: InvoicingPlan, active: z.number().int(), new30: z.number().int(), cancelled30: z.number().int(), quotaReached: z.number().int() })),
  recentIncidents: z.array(SaasIncidentRow),
  recentDocuments: z.array(SaasDocumentRow.extend({ companyId: Id, companyName: z.string() })),
});
export type SaasOverview = z.infer<typeof SaasOverview>;

/* ═════════════════════════ FS-Iopole · e-invoicing flow ═════════════════════════ */

export const FlowStatus = z.enum(["submitted", "in_progress", "rejected", "refused", "paid", "not_transmitted"]);
export type FlowStatus = z.infer<typeof FlowStatus>;
export const FlowPeriod = z.enum(["today", "7d", "30d"]);
export type FlowPeriod = z.infer<typeof FlowPeriod>;

export const FlowQuery = z.object({
  q: z.string().trim().optional(),
  status: FlowStatus.optional(),
  period: FlowPeriod.default("today"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(10),
});
export type FlowQueryInput = z.input<typeof FlowQuery>;

export const FlowRow = z.object({
  documentId: Id,
  number: z.string(),
  companyId: Id,
  companyName: z.string(),
  customer: z.string(),
  totalTtcCents: Cents,
  status: EInvoiceStatus,
  /** « UNACCEPTABLE · 213 ». */
  code: z.string().nullable(),
  message: z.string(),
  at: IsoDateTime,
  retryable: z.boolean(),
});
export type FlowRow = z.infer<typeof FlowRow>;

export const FlowPage = paginated(FlowRow).extend({
  counts: z.object({ all: z.number().int(), submitted: z.number().int(), in_progress: z.number().int(), rejected: z.number().int(), refused: z.number().int(), paid: z.number().int(), not_transmitted: z.number().int() }),
  enrollment: z.object({ registered: z.number().int(), inProgress: z.number().int(), notConnected: z.number().int() }),
  recentActions: z.array(z.object({ number: z.string(), status: EInvoiceStatus, detail: z.string(), at: IsoDateTime })),
});
export type FlowPage = z.infer<typeof FlowPage>;

/* ═════════════════════════ FS-Incidents ═════════════════════════ */

export const IncidentsQuery = z.object({
  q: z.string().trim().optional(),
  type: z.enum(["support", "reminder", "email"]).optional(),
  state: z.enum(["open", "resolved", "all"]).default("open"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type IncidentsQueryInput = z.input<typeof IncidentsQuery>;

export const IncidentsPage = paginated(SaasIncidentRow).extend({
  tiles: z.object({ open: z.number().int(), support: z.number().int(), reminders: z.number().int(), emails: z.number().int(), rejects: z.number().int(), avgResponseHours: z.number().nullable() }),
  counts: z.object({ all: z.number().int(), support: z.number().int(), reminder: z.number().int(), email: z.number().int() }),
});
export type IncidentsPage = z.infer<typeof IncidentsPage>;

export const SaasIncident = SaasIncidentRow.extend({
  plan: InvoicingPlan,
  quotaSummary: z.string().nullable(),
  sender: z.object({ name: z.string(), email: z.string() }).nullable(),
  subject: z.string().nullable(),
  message: z.string().nullable(),
  diagnostic: z.object({ title: z.string(), text: z.string() }).nullable(),
  /** Failed reminder: the invoice to remind. */
  reminder: z.object({ documentId: Id, number: z.string(), customer: z.string(), totalTtcCents: Cents, issuedOn: IsoDate, dueOn: IsoDate, delayDays: z.number().int() }).nullable(),
  /** Rejection: the document to retry from the flow. */
  documentId: Id.nullable(),
  assignee: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  history: z.array(SaasHistoryEvent),
});
export type SaasIncident = z.infer<typeof SaasIncident>;

const ByIncident = z.object({ incidentId: Id });

/* ═════════════════════════ FS-Parametres · product settings ═════════════════════════ */

export const SaasPlanSettings = z.object({
  plan: InvoicingPlan,
  /** Users included (« Sièges »); extra seats at EXTRA_SEAT_PRICE_CENTS. */
  seatsIncluded: z.number().int().min(1).max(50),
});

export const SaasSettings = z.object({
  mentions: z.object({ invoice: z.string(), quote: z.string() }),
  /** The automatic text « Si non fourni, généré automatiquement selon les infos de l'entreprise ». */
  automaticInvoiceMention: z.string(),
  defaultVatRate: z.number(),
  franchiseMention: z.string(),
  numbering: z.array(z.object({ kind: SaasDocKind, format: z.string(), lastNumber: z.string().nullable() })),
  plans: z.array(SaasPlanSettings),
  iopole: z.object({ configured: z.boolean(), apiUrl: z.string(), apiKeySet: z.boolean(), webhookSecretSet: z.boolean() }),
  signature: z.object({ provider: z.string(), packCredits: z.number().int().nullable(), packPriceHtCents: Cents.nullable() }),
  updatedAt: IsoDateTime.nullable(),
});
export type SaasSettings = z.infer<typeof SaasSettings>;

export const SaasSettingsBody = z.object({
  mentions: z.object({ invoice: z.string().trim().max(1000), quote: z.string().trim().max(1000) }),
  defaultVatRate: z.union([z.literal(20), z.literal(10), z.literal(5.5), z.literal(2.1), z.literal(0)]),
});

/* ═════════════════════════ Endpoints ═════════════════════════ */

export const saasAdminApi = {
  platformFeatures: adminEndpoint({ id: "admin.platform.features", method: "GET", path: "/admin/platform/features", response: PlatformFeatures, permission: ["platform", "read"] }),
  setPlatformFeatures: adminEndpoint({
    permission: ["platform", "write"],
    id: "admin.platform.setFeatures",
    method: "PATCH",
    path: "/admin/platform/features",
    body: PlatformFeaturesPatch,
    response: PlatformFeatures,
  }),
  /** NEW: audit trail of the launch switches (who toggled what, when, why). */
  platformHistory: adminEndpoint({ id: "admin.platform.history", method: "GET", path: "/admin/platform/features/history", response: PlatformFeaturesHistory, permission: ["platform", "read"] }),

  /** NEW (board proposal GET /admin/billing-product/overview?month=): one aggregated call for FS-Vue. */
  overview: adminEndpoint({ id: "admin.saas.overview", method: "GET", path: "/admin/saas/overview", query: z.object({ month: Month.optional() }), response: SaasOverview, permission: ["saas", "read"] }),
  /** NEW (proposal GET /admin/billing-product/overview.csv): audited ADMIN_BILLING_OVERVIEW_EXPORT. */
  exportOverview: adminEndpoint({ id: "admin.saas.exportOverview", method: "POST", path: "/admin/saas/overview/export", body: z.object({ month: Month }), response: ExportFile, permission: ["saas", "write"] }),

  /** NEW (proposal GET /admin/billing-accounts): filtered, paginated, counters recomputed with the filters. */
  accounts: adminEndpoint({ id: "admin.saas.accounts", method: "GET", path: "/admin/saas/accounts", query: SaasAccountsQuery, response: SaasAccountsPage, permission: ["saas", "read"] }),
  /** NEW (proposal GET /admin/billing-accounts.csv): same filters, audited. */
  exportAccounts: adminEndpoint({ id: "admin.saas.exportAccounts", method: "POST", path: "/admin/saas/accounts/export", body: SaasAccountsQuery, response: ExportFile, permission: ["saas", "write"] }),
  account: adminEndpoint({ id: "admin.saas.account", method: "GET", path: "/admin/saas/accounts/:companyId", params: ByCompany, response: SaasAccount, permission: ["saas", "read"] }),
  /** NEW: documents of one account (today GET /admin/tenants/:id returns the last 10 invoices only). */
  accountDocuments: adminEndpoint({ id: "admin.saas.accountDocuments", method: "GET", path: "/admin/saas/accounts/:companyId/documents", params: ByCompany, query: SaasDocumentsQuery, response: SaasDocumentsPage, permission: ["saas", "read"] }),
  document: adminEndpoint({ id: "admin.saas.document", method: "GET", path: "/admin/saas/accounts/:companyId/documents/:documentId", params: ByDocument, response: SaasDocument, permission: ["saas", "read"] }),
  /** NEW support variant of POST /factures/:id/unlock-edit: audit DOCUMENT_UNLOCKED_FOR_EDIT + snapshot, client e-mail. */
  unlockDocument: adminEndpoint({ id: "admin.saas.unlockDocument", method: "POST", path: "/admin/saas/accounts/:companyId/documents/:documentId/unlock", params: ByDocument, body: UnlockBody, response: SaasDocumentRow, permission: ["saas", "write"] }),
  /** NEW: regenerate PDF/A-3 + Factur-X and file again through Iopole (503 when IOPOLE_API_KEY is missing). */
  retryTransmission: adminEndpoint({ id: "admin.saas.retryTransmission", method: "POST", path: "/admin/saas/accounts/:companyId/documents/:documentId/einvoicing/retry", params: ByDocument, response: SaasTransmissionRow, permission: ["saas", "write"] }),
  /** NEW: exceptional reset of a START counter, audited with the reason. */
  resetQuota: adminEndpoint({ id: "admin.saas.resetQuota", method: "POST", path: "/admin/saas/accounts/:companyId/quotas/reset", params: ByCompany, body: ResetQuotaBody, response: SaasAccount, permission: ["saas", "write"] }),
  /** Today PATCH /admin/tenants/:id/status (SUSPENDED) + « Compte suspendu » e-mail. */
  suspendAccount: adminEndpoint({ id: "admin.saas.suspendAccount", method: "POST", path: "/admin/saas/accounts/:companyId/suspend", params: ByCompany, body: SuspendBody, response: SaasAccount, permission: ["saas", "write"] }),
  reactivateAccount: adminEndpoint({ id: "admin.saas.reactivateAccount", method: "POST", path: "/admin/saas/accounts/:companyId/reactivate", params: ByCompany, response: SaasAccount, permission: ["saas", "write"] }),
  /** Today GET /admin/tenants/:id/export (JSON, audit ADMIN_TENANT_EXPORT). */
  exportAccount: adminEndpoint({ id: "admin.saas.exportAccount", method: "POST", path: "/admin/saas/accounts/:companyId/export", params: ByCompany, response: ExportFile, permission: ["saas", "write"] }),

  /** NEW: Iopole transmissions across accounts (statuses from the webhooks), « Non transmise » skips logged. */
  flow: adminEndpoint({ id: "admin.saas.flow", method: "GET", path: "/admin/saas/einvoicing/transmissions", query: FlowQuery, response: FlowPage, permission: ["saas", "read"] }),
  exportFlow: adminEndpoint({ id: "admin.saas.exportFlow", method: "POST", path: "/admin/saas/einvoicing/transmissions/export", body: FlowQuery, response: ExportFile, permission: ["saas", "write"] }),

  /** NEW: `invoicing_incidents` (support form, failed reminders, undelivered e-mails, Iopole rejections). */
  incidents: adminEndpoint({ id: "admin.saas.incidents", method: "GET", path: "/admin/saas/incidents", query: IncidentsQuery, response: IncidentsPage, permission: ["saas", "read"] }),
  incident: adminEndpoint({ id: "admin.saas.incident", method: "GET", path: "/admin/saas/incidents/:incidentId", params: ByIncident, response: SaasIncident, permission: ["saas", "read"] }),
  replyIncident: adminEndpoint({
    id: "admin.saas.replyIncident",
    method: "POST",
    path: "/admin/saas/incidents/:incidentId/reply",
    params: ByIncident,
    body: z.object({ subject: z.string().trim().min(1), message: z.string() }),
    response: SaasIncident,
    permission: ["saas", "write"],
  }),
  resolveIncident: adminEndpoint({ id: "admin.saas.resolveIncident", method: "POST", path: "/admin/saas/incidents/:incidentId/resolve", params: ByIncident, body: z.object({ note: z.string().trim().max(500).optional() }), response: SaasIncident, permission: ["saas", "write"] }),
  /** Send the failed reminder by hand (« Relance facture » J+7 template) to the typed address. */
  sendReminder: adminEndpoint({ id: "admin.saas.sendReminder", method: "POST", path: "/admin/saas/incidents/:incidentId/send-reminder", params: ByIncident, body: z.object({ email: Email }), response: SaasIncident, permission: ["saas", "write"] }),

  /** NEW (proposal PUT /admin/product-settings): product defaults. */
  settings: adminEndpoint({ id: "admin.saas.settings", method: "GET", path: "/admin/saas/settings", response: SaasSettings, permission: ["saas", "read"] }),
  updateSettings: adminEndpoint({ id: "admin.saas.updateSettings", method: "PUT", path: "/admin/saas/settings", body: SaasSettingsBody, response: SaasSettings, permission: ["saas", "write"] }),
  /** NEW (proposal PUT /admin/plans/:key): seats included only — prices and START quotas are the contract's grid (PLAN_META). */
  updatePlan: adminEndpoint({ id: "admin.saas.updatePlan", method: "PUT", path: "/admin/saas/plans/:plan", params: z.object({ plan: InvoicingPlan }), body: SaasPlanSettings.omit({ plan: true }), response: SaasSettings, permission: ["saas", "write"] }),
  /** NEW (proposal POST /admin/integrations/iopole/test): ping with the configured key, no document sent. */
  testIopole: adminEndpoint({ id: "admin.saas.testIopole", method: "POST", path: "/admin/saas/iopole/test", response: z.object({ ok: z.literal(true), latencyMs: z.number().int() }), permission: ["saas", "write"] }),
} satisfies EndpointGroup;

